import plus16data from '@/data/plus16data.json'
import minus16data from '@/data/minus16data.json'

export type QuestionnaireRun = {
  date: Date
  questionnaireId: number
  totalScore: number,
  answers: {
    answer: number
  }[]
}

type User = {
  email: string
  password: string
  currentUserData: string
  ownData: QuestionnaireUserData|null
  tutoredMinorData: QuestionnaireUserData[]
}

type LegacyUser = {
  email: string
  password: string
  birthdate?: Date
  questionnaireRuns?: QuestionnaireRun[]
}

export type ImportantDate = {
  date: Date,
  title: string
}

type ResourceClicked = {
  date: Date,
  resourceId: number
}

export type QuestionnaireUserData = {
  name: string
  birthdate: Date
  age: number
  importantDates: ImportantDate[]
  questionnaireRuns: QuestionnaireRun[]
  resourcesClicked: ResourceClicked[]
}

type Question = {
  body: string
  comment: string
  image: string
}

export type Questionnaire = {
  id: number
  title: string
  comment: string
  questions: Question[]
  results: {
    condition: string
    text: string
    comment: string
  }[]
  resources: {
    condition: string
    text: string
    type: string
    link: string
  }[]
}

export enum ResourceType {
  Image,
  Video,
  Document
}

export type ResourceData = {
  id: number
  title: string
  type: ResourceType
  url: string
}

export class NotFoundError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "NotFoundError";
    }
}

export class dataManager {

  private static instance = new dataManager();

  public welcomeMessageAlreadyShown = false

  public static getInstance(): dataManager {
    return this.instance;
  }

  public static setInstance(dataManagerInstance: dataManager): void {
    this.instance = dataManagerInstance
  }

  private user : User|null = null;

  private questionnaireUserData: QuestionnaireUserData|null = null;

  public constructor() {
    this.migrateLocalStorageDataFormat()

    const email = window.localStorage.getItem("currentUser");
    if (email !== null) {
      this.user = this.recoverUserData(email)
      this.questionnaireUserData = this.findCurrentUserData(this.user)
    }
  }

  private findCurrentUserData(user: User): QuestionnaireUserData {
    if (user.currentUserData === '__yourself__') {
      if (user.ownData === null) {
        throw new Error("Wrong current data set")
      }
      return user.ownData
    } else {
      for (let i = 0; i < user.tutoredMinorData.length; i++) {
        if (user.tutoredMinorData[i].name === user.currentUserData) {
          return user.tutoredMinorData[i]
        }
      }
      throw new Error("User data name not found: " + user.currentUserData)
    }
  }

  private recoverUserData(email: string): User {
    const userString = window.localStorage.getItem(email)
    if (! userString) {
      throw new Error("User doesnt exist")
    }
    const user = JSON.parse(userString, dataManager.dateTimeReviver)
    this.migrateUser(user)
    return user
  }

  public getCurrentUser(): User|null {
    return this.user;
  }

  public getCurrentUserData(): QuestionnaireUserData {
    if (!this.questionnaireUserData) {
      throw new Error("No user logged in")
    }
    return this.questionnaireUserData
  }

  private getCurrentUserInitialAge(): number {
    if (!this.questionnaireUserData) {
      throw new Error("No user logged in")
    }
    return this.questionnaireUserData.age
  }

  private getCurrentUserAge(): number {
    if (! this.questionnaireUserData) {
      throw new Error("No user logged in")
    }
    return dataManager.calculateAge(this.questionnaireUserData.birthdate)
  }

  public static calculateAge(birthdate: Date): number {
    // birthdate must be interpreted as UTC
    // today as local, since we will consider you add years when the right day
    // arrived in you local time
    const today = new Date()
    let age = today.getFullYear() - birthdate.getUTCFullYear()
    const m = today.getMonth() - birthdate.getUTCMonth()

    if (m < 0 || (m === 0 && today.getDate() < birthdate.getUTCDate())) {
        age--;
    }
    return age;
  }

  public existsUser(email: string): boolean {
    return localStorage.getItem(email) !== null;
  }

  private migrateLocalStorageDataFormat(): void {
    let userListString = window.localStorage.getItem("userList");
    if (userListString) {
      let userList = JSON.parse(userListString, dataManager.dateTimeReviver)
      window.localStorage.removeItem("userList")
      userList.forEach((user: User) => {
        this.migrateUser(user)
        window.localStorage.setItem(user.email, JSON.stringify(user))
      })
    }
    let currentUserString = window.localStorage.getItem("currentUser")
    if (currentUserString) {
      try {
        let currentUser = JSON.parse(currentUserString)
        if (currentUser && typeof currentUser === 'object') {
          window.localStorage.setItem("currentUser", currentUser.email);
        }
      } catch (error) {
        // probably already converted
      }
    }
  }

  private migrateUser(user: User): void {
    if (user.hasOwnProperty('birthdate')) {
      let unknownUser = user as unknown;
      let legacyUser = unknownUser as LegacyUser
      if (! legacyUser.birthdate) {
        throw new Error("No birthdate in legacy user")
      }
      let age = dataManager.calculateAge(legacyUser.birthdate)
      if (!legacyUser.hasOwnProperty('questionnaireRuns') || !legacyUser.questionnaireRuns) {
        legacyUser.questionnaireRuns = []
      }
      let questionnaireUserData: QuestionnaireUserData = {
        birthdate: legacyUser.birthdate,
        age: age,
        name: age >= 16? '__yourself__' : 'Tu hijo/a',
        importantDates: [],
        questionnaireRuns: legacyUser.questionnaireRuns,
        resourcesClicked: []
      }
      user.currentUserData = questionnaireUserData.name
      if (age >= 16) {
        user.ownData = questionnaireUserData
        user.tutoredMinorData = []
      } else {
        user.ownData = null
        user.tutoredMinorData = [questionnaireUserData]
      }
      delete legacyUser.birthdate
      delete legacyUser.questionnaireRuns
    }

    if (user.ownData && ! user.ownData.hasOwnProperty("importantDates")) {
      user.ownData.importantDates = []
    }
    if (user.ownData && ! user.ownData.hasOwnProperty("resourcesClicked")) {
      user.ownData.resourcesClicked = []
    }
    user.tutoredMinorData.forEach((questionnaireUserData) => {
      if (!questionnaireUserData.hasOwnProperty("importantDates")) {
        questionnaireUserData.importantDates = []
      }
      if (!questionnaireUserData.hasOwnProperty("resourcesClicked")) {
        questionnaireUserData.resourcesClicked = []
      }
    })
  }

  public loginUser(email: string, password: string): boolean {
    try {
      let user = this.recoverUserData(email)
      if (user.password !== password) {
        // wrong password
        return false
      }

      this.user = user
      this.questionnaireUserData = this.findCurrentUserData(user)

      window.localStorage.setItem("currentUser", email)
      return true;
    } catch (error: any) {
      if (error.message === "User doesnt exist") {
        return false
      } else {
        throw error
      }
    }
  }

  public registerUser(
    email: string,
    password: string,
    ownBirthdate: Date|null,
    minorData: {name: string, birthdate: Date}[]): boolean {

    if (this.existsUser(email)) {
        return false
    }

    let newUser: User = {
      email: email,
      currentUserData: '',
      password: password,
      ownData: null,
      tutoredMinorData: []
    }

    if (ownBirthdate) {
      newUser.ownData = {
        name: '__yourself__',
        birthdate: ownBirthdate,
        age: dataManager.calculateAge(ownBirthdate),
        importantDates: [],
        questionnaireRuns: [],
        resourcesClicked: []
      }
      newUser.currentUserData = '__yourself__'
    }

    for (let i=0; i < minorData.length; i++) {
      newUser.tutoredMinorData.push({
        name: minorData[i].name,
        birthdate: minorData[i].birthdate,
        age: dataManager.calculateAge(minorData[i].birthdate),
        importantDates: [],
        questionnaireRuns: [],
        resourcesClicked: []
      })
      if (! newUser.currentUserData) {
        newUser.currentUserData = minorData[i].name
      }
    }

    this.persistUser(newUser)
    return true
  }

  public removeUser(email: string): void {
    window.localStorage.removeItem(email);
  }

  public logout(): void {
    this.user = null;
    window.localStorage.removeItem("currentUser");
  }

  public isAdult(): boolean {
    return ! this.isTutoredMinor()
  }

  public isTutoredMinor(): boolean {
    return this.getCurrentUserInitialAge() < 16;
  }

  public getQuestionnaireList(): Questionnaire[] {
    if (this.isTutoredMinor()) {
      return minus16data as Questionnaire[];
    } else {
      return plus16data as Questionnaire[];
    }
  }

  public getMainQuestionnaireList(): Questionnaire[] {
    return this.filterMainQuestionnaires(this.getQuestionnaireList())
  }

  public getSubQuestionnaireList(id: number): Questionnaire[] {
    const mainQuestionnaire = this.getQuestionnaire(id)
    const subQuestionnaires: Questionnaire[] = []
    if (mainQuestionnaire.title.includes(' - ')) {
      const mainTitle = mainQuestionnaire.title.substring(0, mainQuestionnaire.title.indexOf(' - ') + 3)
      this.getQuestionnaireList().forEach((questionnaire) => {
        if (questionnaire.title.startsWith(mainTitle)) {
          subQuestionnaires.push(questionnaire)
        }
      })
    } else {
      subQuestionnaires.push(mainQuestionnaire)
    }
    return subQuestionnaires
  }

  /** Returns only main questionnaires */
  private filterMainQuestionnaires(questionnaires: Questionnaire[]): Questionnaire[] {
    const mainQuestionnaires: Questionnaire[] = []
    questionnaires.forEach((questionnaire) => {
      if (this.isMainQuestionnaire(questionnaire)) {
        mainQuestionnaires.push(questionnaire)
      }
    })
    return mainQuestionnaires
  }

  private isMainQuestionnaire(questionnaire: Questionnaire): boolean {
    if (questionnaire.title.includes(' - ')) {
      const mainTitle = questionnaire.title.substring(0, questionnaire.title.indexOf(' - ') + 3)
      const allQuestionnaires = this.getQuestionnaireList()
      for (let i=0; i < allQuestionnaires.length; i++) {
        if (allQuestionnaires[i].id == questionnaire.id) {
          if (i == 0) {
            return true
          }
          return ! allQuestionnaires[i-1].title.startsWith(mainTitle)
        }
      }
      throw new Error('questionnaire not found:' + questionnaire.id)
    } else {
      return true
    }
  }

  public getQuestionnaire(id: number): Questionnaire {
    if (this.isTutoredMinor()) {
      return dataManager.findQuestionnaire(id, minus16data);
    } else {
      return dataManager.findQuestionnaire(id, plus16data)
    }
  }

  private static findQuestionnaire(id: number, list: Questionnaire[]): Questionnaire {
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === id) {
        return list[i];
      }
    }
    throw new NotFoundError(`Cuestionario ${id} no disponible`);
  }

  public saveQuestionnaireResults(id: number, questionnaireAnswers: {answer: number}[]): void {
    let score: number = 0;
    questionnaireAnswers.forEach((answer) => {
      score += answer.answer;
    })
    let result = {
      date: new Date(),
      questionnaireId: id,
      totalScore: score,
      answers: questionnaireAnswers
    }
    if (this.user == null || this.questionnaireUserData == null) {
      throw new Error("No user loged in")
    }
    this.questionnaireUserData.questionnaireRuns.push(result);
    this.persist()
  }

  private static dateTimeReviver(key: string, value: unknown): unknown {
    if (key === 'birthdate' || key === 'date') {
      if (typeof value !== 'string') {
        throw new Error("Date with wrong type: " + typeof value)
      }
      let date = new Date(value)
      if (isNaN(date.getTime())) {
        throw new Error("Date wrong format: " + value)
      }
      return date
    } else {
      return value
    }
  }

  public getUserDatas(): string[] {
    let userDatas: string[] = []
    if (this.getCurrentUser()?.ownData) {
      userDatas.push('__yourself__')
    }
    this.getCurrentUser()?.tutoredMinorData.forEach(minorData => {
      userDatas.push(minorData.name)
    })
    return userDatas
  }

  public setCurrentUserData(userDataName: string): void {
    let user = this.getCurrentUser()
    if (!user) {
      throw new Error('No user logged in')
    }
    user.currentUserData = userDataName
    this.questionnaireUserData = this.findCurrentUserData(user)

    this.persist()
  }

  /** Will refer to any subquestionnaires, too */
  public getLastQuestionnaireRun(questionnaireId: number|null): QuestionnaireRun|null {
    let questionnaireRuns = this.getCurrentUserData().questionnaireRuns
    let lastQuestionnaireRun: QuestionnaireRun|null = null
    let questionnaireIds: number[] = []
    if (questionnaireId != null) {
      questionnaireIds = this.getSubquestionnaireIds(questionnaireId)
    }
    for (let i=0; i < questionnaireRuns.length; i++) {
      if (
        (questionnaireIds.includes(questionnaireRuns[i].questionnaireId) || questionnaireId == null) &&
        (lastQuestionnaireRun == null || lastQuestionnaireRun.date.getTime() < questionnaireRuns[i].date.getTime())
      ) {
        lastQuestionnaireRun = questionnaireRuns[i]
      }
    }
    return lastQuestionnaireRun
  }

  private getSubquestionnaireIds(id: number) {
    const subQuestionnaireIds: number[] = []
    this.getSubQuestionnaireList(id).forEach((questionnaire) => {
      subQuestionnaireIds.push(questionnaire.id)
    })
    return subQuestionnaireIds
  }

  /** Will refer to any subquestionnaires, too */
  public getLastTimeDone(questionnaireId: number|null): Date|null {
    const lastQuestionnaireRun = this.getLastQuestionnaireRun(questionnaireId)
    if (lastQuestionnaireRun == null) {
      return null
    } else {
      return lastQuestionnaireRun.date
    }
  }

  public getLastTimeDays(questionnaireId: number | null): number|null {
    const lastTimeDone = this.getLastTimeDone(questionnaireId)
    if (lastTimeDone == null) {
      return null
    }
    const today = new Date()

    const msInDay = 24 * 60 * 60 * 1000;
    const days = Math.round((today.getTime() - lastTimeDone.getTime()) / msInDay)
    return days
  }

  /** Only main questionnaires */
  public getDoneQuestionnaires(): Questionnaire[] {
    const doneQuestionaires: Questionnaire[] = []
    const questionnaires = this.getMainQuestionnaireList()
    for (let i=0; i < questionnaires.length; i++) {
      if (! this.isDone(questionnaires[i])) {
        doneQuestionaires.push(questionnaires[i])
      }
    }
    this.sortQuestionnaires(doneQuestionaires)
    return doneQuestionaires
  }

  /** Only main questionnaires */
  public getPendingQuestionnaires(): Questionnaire[] {
    const pendingQuestionaires: Questionnaire[] = []
    const questionnaires = this.getMainQuestionnaireList()
    for (let i=0; i < questionnaires.length; i++) {
      if (this.isDone(questionnaires[i])) {
        pendingQuestionaires.push(questionnaires[i])
      }
    }
    this.sortQuestionnaires(pendingQuestionaires)
    return pendingQuestionaires
  }

  private isDone(questionnaire: Questionnaire): boolean {
    const lastRunDays = this.getLastTimeDays(questionnaire.id)
    return (lastRunDays == null || lastRunDays > 30 * 3)
  }

  private sortQuestionnaires(list: Questionnaire[]) {
    list.sort((a, b) => {
      return this.dateSort(this.getLastTimeDone(a.id), this.getLastTimeDone(b.id))
    })
  }

  public calculateWarningTexts(questionnaire: Questionnaire, questionnaireRun: QuestionnaireRun|null): string[] {
    const warnings: string[] = []
    if (questionnaireRun == null) {
      return warnings;
    }

    let lastWarning = ''

    questionnaire.results.forEach((result) => {
      if (this.conditionMatches(result.condition, questionnaireRun)) {
        if (result.text != lastWarning) {
          warnings.push(result.text)
          lastWarning = result.text
        }
      }
    })
    return warnings
  }

  public getAllResources(questionnaireId: number) : ResourceData[] {
    const resources : ResourceData[] = []
    const questionnaire = this.getQuestionnaire(questionnaireId)
    questionnaire.resources.forEach((resource) => {
      resources.push({
        id: 0,
        title: resource.text,
        url: (resource.link.startsWith('http') ? resource.link : '/' + resource.link),
        type: this.resourceType(resource.type)
      })
    })
    return resources
  }

  public calculateRecommendedResources(questionnaireRun: QuestionnaireRun|null): ResourceData[] {
    const resources : ResourceData[] = []
    if (questionnaireRun == null) {
      return resources
    }

    const questionnaire = this.getQuestionnaire(questionnaireRun.questionnaireId)
    questionnaire.resources.forEach((resource) => {
      if (this.conditionMatches(resource.condition, questionnaireRun)) {
        resources.push({
          id: 0,
          title: resource.text,
          url: (resource.link.startsWith('http') ? resource.link : '/' + resource.link),
          type: this.resourceType(resource.type)
        })
      }
    })
    return resources
  }

  private resourceType(stringType: string): ResourceType {
    if (stringType == "document") {
      return ResourceType.Document
    } else if (stringType == "video") {
      return ResourceType.Video
    } else if (stringType == "image") {
      return ResourceType.Image
    } else {
      throw new Error('Invalid resource type:' + stringType)
    }
  }

  private conditionMatches(condition: string, questionnaireRun: QuestionnaireRun): boolean {
    let conditionParts = condition.split(" ")
    if (condition.startsWith("P")) {
      let questionNumber = Number.parseInt(conditionParts[0].substring(1))
      let threshold = Number.parseInt(conditionParts[2])
      return questionnaireRun.answers[questionNumber - 1].answer > threshold
    } else {
        let operator = conditionParts[0].trim()
        let threshold = Number.parseInt(conditionParts[1])
        if (operator == "<=") {
          return questionnaireRun.totalScore <= threshold
        } else if (operator == ">") {
          return questionnaireRun.totalScore > threshold
        } else {
          throw new Error("Bad operator: " + operator)
        }
    }
  }

  /** Only main questionnaires, but adding subquestionnaire runs */
  public getAllQuestionnaireRuns(quesetionnaireId : number): QuestionnaireRun[] {
    const questionnaireRuns: QuestionnaireRun[] = []
    const questionnaireIds: number[] = this.getSubquestionnaireIds(quesetionnaireId)
    this.getCurrentUserData().questionnaireRuns.forEach((questionnaireRun) => {
      if (questionnaireIds.includes(questionnaireRun.questionnaireId)) {
        questionnaireRuns.push(questionnaireRun)
      }
    })

    this.sortQuestionnaireRuns(questionnaireRuns)
    return questionnaireRuns
  }

  private sortQuestionnaireRuns(list: QuestionnaireRun[]) {
    list.sort((a, b) => {
      return this.dateSort(a.date, b.date);
    })
  }

  private dateSort(dateA: Date|null, dateB: Date|null): number {
      if (dateA == null && dateB == null) {
        return 0
      }
      if (dateA == null) {
        return -1
      }
      if (dateB == null) {
        return 1
      }
      if (dateA.getTime() > dateB.getTime()) {
        return -1
      }
      if (dateA.getTime() < dateB.getTime()) {
        return 1
      }
      return 0
  }

  public saveImportantDates(importantDates: ImportantDate[]): void {
    this.getCurrentUserData().importantDates = importantDates
    this.persist()
  }

  private persist(): void {
    if (this.user == null) {
      throw new Error('No user')
    }
    this.persistUser(this.user)
  }

  private persistUser(user: User): void {
    localStorage.setItem(user.email, JSON.stringify(user))
    if (import.meta.env.VITE_ENV_VARIABLE != 'development') {
      const enc = new TextEncoder()
      const userDataEncoded = enc.encode(JSON.stringify(user))
      navigator.sendBeacon('/api/save.php', userDataEncoded)
    }
  }

  public cleanAllData(): void {
    this.logout()
    localStorage.clear()
  }

  public cleanRunsData(): void {
    this.getCurrentUserData().questionnaireRuns = []
    this.persist()
  }

  public generateRandomResults(): void {
    const currentUserData = this.getCurrentUserData()
    // Create 1 to 4 answers for each questionnaire
    // start in 1/1/2025 and advance between 14 and 90 days
    // or start in last run on that questionnaire
    this.getQuestionnaireList().forEach((questionnaire) => {
      const startRunDate: Date = new Date('2025-01-01')
      const numRunsToGenerate = this.getRandomInt(1, 4)
      const minPoints = questionnaire.questions.length
      const maxPoints = questionnaire.questions.length * 5

      const oneDayMillis = 24 * 60 * 60 * 1000 // hours*minutes*seconds*milliseconds
      const today = new Date()
      const maxDiffDays = Math.round(Math.abs((today.getTime() - startRunDate.getTime()) / oneDayMillis));

      for (let i=0; i < numRunsToGenerate; i++) {
        let nextRunDate = new Date(startRunDate.toISOString())
        nextRunDate.setDate(nextRunDate.getDate() + this.getRandomInt(1, maxDiffDays))
        currentUserData.questionnaireRuns.push({
          questionnaireId: questionnaire.id,
          date: nextRunDate,
          answers: [],
          totalScore: this.getRandomInt(minPoints, maxPoints)
        })
      }
    })
    this.persist()
  }

  private getRandomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  public extractTitles(wholeTitle: string): string[] {
    if (wholeTitle.includes(" - ")) {
      let titles = wholeTitle.split(" - ", 2)
      return [titles[0], titles[1]]
    } else {
      return [wholeTitle, wholeTitle]
    }
  }

  public normalizeToPercentage(questionnaireRun: QuestionnaireRun): number {
    const totalQuestions = this.getQuestionnaire(questionnaireRun.questionnaireId).questions.length
    const minScore = totalQuestions
    const maxScore = totalQuestions * 5
    return 100 * (questionnaireRun.totalScore - minScore) / (maxScore - minScore)
  }

  public registerResourceClicked(resourceId: number): void {
    const userData = this.getCurrentUserData()
    userData.resourcesClicked.push({
      date: new Date(),
      resourceId: resourceId
    })
    this.persist()
  }
}
